"use client";

import Image from "next/image";
import { useState } from "react";

import {
  EMAIL_REGEX,
  isValidWebsiteUrl,
  logGAEvent,
  mergeClasses,
} from "@trex-2b/libs";
import { trexAuthProxy } from "@trex-2b/libs/config";
import {
  ONBOARDING_FLOWS,
  reportGrowthPortalError,
} from "@trex-2b/libs/utils/bugsnag-report";
import { Toast } from "@trex-2b/ui";

import { Button } from "@/components/button";
import { CommonTextInput } from "@/components/common-text-input";

type HelpOptionId = "get-users" | "drive-volume" | "build-community";

const HELP_OPTIONS: { id: HelpOptionId; label: string }[] = [
  { id: "get-users", label: "Get users" },
  { id: "drive-volume", label: "Drive volume" },
  { id: "build-community", label: "Build community" },
];

export type ContactSalesFormCardProps = {
  className?: string;
  /** After successful submit — parent switches to thanks card (Figma flow). */
  onSubmitted?: () => void;
};

export function ContactSalesFormCard({
  className,
  onSubmitted,
}: ContactSalesFormCardProps) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [help, setHelp] = useState<HelpOptionId>(HELP_OPTIONS[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);

  const selectHelpOption = (optionId: HelpOptionId) => {
    setHelp(optionId);
  };

  const handleSubmitClick = async () => {
    const trimmedEmail = email.trim();
    const trimmedWebsite = website.trim();
    if (!trimmedEmail || isSubmitting) return;
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("Please enter a valid work email.");
      return;
    }
    if (!isValidWebsiteUrl(trimmedWebsite)) {
      setWebsiteError("Please enter a valid website URL.");
      return;
    }

    setEmailError(null);
    setWebsiteError(null);
    setIsSubmitting(true);

    try {
      const selectedGoal = HELP_OPTIONS.find((option) => option.id === help);
      const selectedGoals = selectedGoal ? [selectedGoal.label] : [];

      const response = await trexAuthProxy.v1.submitLandingContact({
        email: trimmedEmail,
        websiteUrl: trimmedWebsite || undefined,
        projectGoals: selectedGoals.length ? selectedGoals : undefined,
      });

      // 错误码 9022 表示触发限频。该场景不应展示通用提交失败提示，产品希望被限频时让用户感知正常提交，因此在此处单独放行。
      if (!response.data?.success && response.data.code !== "9022") {
        reportGrowthPortalError(
          ONBOARDING_FLOWS.LANDING_CONTACT_SALES,
          new Error(response.data?.message || "Submit failed"),
          {
            lead_type: "landing_contact_sales",
            response_code: response.data?.code,
          },
        );
        logGAEvent("contact_sales_submit_fail", {
          lead_type: "landing_contact_sales",
        });
        Toast.show({
          type: "error",
          message: "Submit failed. Please try again.",
        });
        return;
      }

      logGAEvent("contact_sales_submit_success", {
        lead_type: "landing_contact_sales",
        website_url: trimmedWebsite,
        goals: selectedGoals,
      });
      onSubmitted?.();
    } catch (error) {
      console.error("Landing contact sales submit failed", error);
      reportGrowthPortalError(ONBOARDING_FLOWS.LANDING_CONTACT_SALES, error, {
        lead_type: "landing_contact_sales",
      });
      logGAEvent("contact_sales_submit_fail", {
        lead_type: "landing_contact_sales",
        error_type: error instanceof Error ? error.name : "UnknownError",
      });
      Toast.show({
        type: "error",
        message: "Submit failed. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={mergeClasses(
        "flex h-[562px] w-[497px] shrink-0 flex-col justify-between rounded-lg border border-solid border-[rgba(28,25,22,0.4)] bg-white px-6 pb-6 pt-[34px]",
        className,
      )}
    >
      <div className="flex w-full flex-col gap-6">
        <div className="relative size-[65px] shrink-0 overflow-hidden">
          <div className="absolute inset-[2.47%_6.35%_2.51%_6.35%]">
            <Image
              alt=""
              src={"/images/landing/contact-sale-smail-default.svg"}
              width={56}
              height={60}
              className="block size-full max-w-none object-contain"
            />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center">
          <p className="whitespace-nowrap text-[28px] font-medium leading-[30.8px] tracking-[-0.3px] text-[#31373d]">
            Tell us a bit about you
          </p>
        </div>
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col">
            <div className="flex w-[365px] shrink-0 items-center mb-[18px]">
              <p className="whitespace-nowrap text-[16px] font-medium leading-5 tracking-[0.14px] text-black">
                What’s your work email?
              </p>
            </div>
            <div className="relative h-[46px] w-[449px] shrink-0">
              <CommonTextInput
                type="email"
                autoComplete="email"
                placeholder="you@project.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                data-testid="contact-sales-email-input"
                className="absolute inset-0"
              />
            </div>
            {emailError ? (
              <p className="text-sm text-red-600">{emailError}</p>
            ) : null}
          </div>
          <div className="flex w-full flex-col gap-[18px]">
            <div className="flex w-[365px] shrink-0 items-center">
              <p className="whitespace-nowrap text-[16px] font-medium leading-5 tracking-[0.14px] text-black">
                What’s your website URL?
              </p>
            </div>
            <CommonTextInput
              type="url"
              autoComplete="url"
              placeholder="xxx.com"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                if (websiteError) setWebsiteError(null);
              }}
              data-testid="contact-sales-website-input"
              className="w-full"
            />
            {websiteError ? (
              <p className="text-sm text-red-600 -mt-2">{websiteError}</p>
            ) : null}
          </div>
          <div className="flex w-full flex-col gap-[18px]">
            <div className="flex w-[365px] shrink-0 items-center">
              <p className="whitespace-nowrap text-[16px] font-medium leading-5 tracking-[0.14px] text-black">
                How can we help you?
              </p>
            </div>
            <div
              className="flex flex-wrap gap-1.5"
              role="radiogroup"
              aria-label="How can we help you?"
            >
              {HELP_OPTIONS.map(({ id, label }) => {
                const selected = help === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`contact-sales-help-${id}-option`}
                    onClick={() => selectHelpOption(id)}
                    className={mergeClasses(
                      "flex h-8 shrink-0 items-center justify-center rounded-[55px] border border-solid border-[#aaa] px-3 cursor-pointer",
                      "text-[16px] font-medium leading-5 tracking-[0.14px] transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(28,25,22,0.35)] focus-visible:ring-offset-2",
                      selected
                        ? "bg-[#1c1916] text-white"
                        : "bg-white text-[rgba(28,25,22,0.8)]",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Button
        type="button"
        variant="Fill"
        size="XL"
        fullWidth
        className="text-sm font-bold leading-5 tracking-[0.14px]"
        disabled={!email.trim() || !website.trim() || isSubmitting}
        data-testid="contact-sales-submit-btn"
        onClick={handleSubmitClick}
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </Button>
    </div>
  );
}
